/**
 * Role defaults of the Backoffice entry gates — PURE, no I/O.
 *
 * These are the sets of roles that may OPEN a section of the Backoffice, lifted out of
 * each feature's `permissions.ts` so the menu-access registry (menuAccess.ts) and the
 * screens read ONE definition. The server (RLS) stays the real authority: the sets only
 * decide which entries a role is offered, so nobody is shown a section whose data the
 * RLS would refuse.
 *
 * Each feature's permissions file re-exports its constant from here; its write sets
 * (what a role may CHANGE once inside) stay in the app, next to the screens that use them.
 */

import type { UserRole } from '../enums/index.js';

/** Roles that may OPEN the settings screen at all (admin or production_manager). */
export const SETTINGS_VIEW_ROLES = [
  'admin',
  'production_manager',
] as const satisfies readonly UserRole[];

/** Roles that may OPEN the Compres section at all. */
export const PURCHASES_VIEW_ROLES = ['admin', 'purchasing'] as const satisfies readonly UserRole[];

/** Roles that may OPEN the Vendes section at all. */
export const SALES_VIEW_ROLES = ['admin', 'sales'] as const satisfies readonly UserRole[];

/** Roles that may OPEN and run the Smart Importer (admin only). */
export const IMPORTER_ROLES = ['admin'] as const satisfies readonly UserRole[];

/**
 * Roles allowed to view the activity log tab (admin ONLY). The whole obrador
 * audit trail is an administration oversight tool: even a production_manager (who
 * may open Settings) does not see this tab. RLS on audit_log additionally lets
 * quality read it, but the product decision here is admin-only in the UI.
 */
export const AUDIT_LOG_VIEW_ROLES = ['admin'] as const satisfies readonly UserRole[];

/** Roles that may OPEN the Qualitat screens (no conformitats, autocontrols, evidències). */
export const QUALITY_VIEW_ROLES = [
  'admin',
  'quality',
  'production_manager',
] as const satisfies readonly UserRole[];

/** Roles that may WRITE quality records (homologar, plantilles, formació, tancar NC). */
export const QUALITY_WRITE_ROLES = ['admin', 'quality'] as const satisfies readonly UserRole[];

/**
 * Roles that may OPEN the Recompte d'existències screens and run a count (create, count, validate, cancel): the same
 * as the stock adjustment (`adjust_stock`: admin, production_manager, purchasing). The RPCs check it on the server.
 */
export const STOCK_COUNT_ROLES = [
  'admin',
  'production_manager',
  'purchasing',
] as const satisfies readonly UserRole[];
